import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
const GoogleIcon = () => (_jsxs("svg", { className: "size-5", viewBox: "0 0 24 24", children: [_jsx("path", { fill: "#4285F4", d: "M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" }), _jsx("path", { fill: "#34A853", d: "M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" }), _jsx("path", { fill: "#FBBC05", d: "M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" }), _jsx("path", { fill: "#EA4335", d: "M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" })] }));
var AuthMode;
(function (AuthMode) {
    AuthMode[AuthMode["SignIn"] = 0] = "SignIn";
    AuthMode[AuthMode["Register"] = 1] = "Register";
    AuthMode[AuthMode["ForgotPassword"] = 2] = "ForgotPassword";
})(AuthMode || (AuthMode = {}));
export function LoginForm({ className, onEmailLogin, onRegister, onGoogleLogin, onForgotPassword, error: externalError, labels = {}, ...props }) {
    const l = {
        signIn: labels.signIn ?? 'Sign in',
        register: labels.register ?? 'Create account',
        email: labels.email ?? 'Email',
        password: labels.password ?? 'Password',
        name: labels.name ?? 'Name',
        forgotPassword: labels.forgotPassword ?? 'Forgot password?',
        newAccount: labels.newAccount ?? 'New account',
        alreadyHaveAccount: labels.alreadyHaveAccount ?? 'Already have an account?',
        signInWithGoogle: labels.signInWithGoogle ?? 'Sign in with Google',
        sendResetLink: labels.sendResetLink ?? 'Send reset link',
        backToSignIn: labels.backToSignIn ?? 'Back to sign in',
        resetEmailSent: labels.resetEmailSent ?? 'A password reset link has been sent to your email.',
        or: labels.or ?? 'or',
        signingIn: labels.signingIn ?? 'Signing in...'
    };
    const [mode, setMode] = useState(AuthMode.SignIn);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const [internalError, setInternalError] = useState(null);
    const [resetEmailSent, setResetEmailSent] = useState(false);
    const error = externalError ?? internalError;
    const handleSignIn = async (e) => {
        e.preventDefault();
        if (!onEmailLogin)
            return;
        setInternalError(null);
        setLoading(true);
        try {
            await onEmailLogin(email, password);
        }
        catch (err) {
            setInternalError(err.message || 'Something went wrong');
        }
        finally {
            setLoading(false);
        }
    };
    const handleRegister = async (e) => {
        e.preventDefault();
        if (!onRegister)
            return;
        setInternalError(null);
        setLoading(true);
        try {
            await onRegister(email, password, name);
        }
        catch (err) {
            setInternalError(err.message || 'Something went wrong');
        }
        finally {
            setLoading(false);
        }
    };
    const handleGoogleLogin = async () => {
        if (!onGoogleLogin)
            return;
        setInternalError(null);
        setLoading(true);
        try {
            await onGoogleLogin();
        }
        catch (err) {
            setInternalError(err.message || 'Something went wrong');
        }
        finally {
            setLoading(false);
        }
    };
    const handleForgotPassword = async (e) => {
        e.preventDefault();
        if (!onForgotPassword)
            return;
        setInternalError(null);
        setLoading(true);
        try {
            await onForgotPassword(email);
            setResetEmailSent(true);
        }
        catch (err) {
            setInternalError(err.message || 'Something went wrong');
        }
        finally {
            setLoading(false);
        }
    };
    const switchMode = (newMode) => {
        setMode(newMode);
        setInternalError(null);
        setResetEmailSent(false);
    };
    const renderError = () => error ? (_jsx(Alert, { variant: "destructive", className: "mb-2", children: _jsx(AlertDescription, { children: error }) })) : null;
    const renderGoogleDivider = () => onGoogleLogin ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: "relative my-6", children: [_jsx("div", { className: "absolute inset-0 flex items-center", children: _jsx(Separator, {}) }), _jsx("div", { className: "relative flex justify-center", children: _jsx("span", { className: "bg-background px-2 text-sm text-muted-foreground", children: l.or }) })] }), _jsxs(Button, { variant: "outline", className: "w-full", onClick: handleGoogleLogin, disabled: loading, children: [_jsx(GoogleIcon, {}), l.signInWithGoogle] })] })) : null;
    const renderSignIn = () => (_jsxs("form", { onSubmit: handleSignIn, children: [_jsx("h1", { className: "text-lg font-semibold mb-2", children: l.signIn }), renderError(), _jsxs("div", { className: "mt-4", children: [_jsx("label", { className: "text-sm font-medium", children: l.email }), _jsx(Input, { type: "email", className: "mt-1", value: email, onChange: (e) => setEmail(e.target.value), required: true })] }), _jsxs("div", { className: "mt-4", children: [_jsx("label", { className: "text-sm font-medium", children: l.password }), _jsx(Input, { type: "password", className: "mt-1", value: password, onChange: (e) => setPassword(e.target.value), required: true })] }), _jsx(Button, { type: "submit", className: "w-full mt-4", disabled: loading, children: loading ? l.signingIn : l.signIn }), _jsxs("div", { className: "flex justify-between mt-4", children: [onRegister && (_jsx(Button, { variant: "ghost", type: "button", onClick: () => switchMode(AuthMode.Register), disabled: loading, children: l.newAccount })), onForgotPassword && (_jsx(Button, { variant: "ghost", type: "button", onClick: () => switchMode(AuthMode.ForgotPassword), disabled: loading, children: l.forgotPassword }))] }), renderGoogleDivider()] }));
    const renderRegister = () => (_jsxs("form", { onSubmit: handleRegister, children: [_jsx("h1", { className: "text-lg font-semibold mb-2", children: l.register }), renderError(), _jsxs("div", { className: "mt-4", children: [_jsx("label", { className: "text-sm font-medium", children: l.name }), _jsx(Input, { type: "text", className: "mt-1", value: name, onChange: (e) => setName(e.target.value), required: true })] }), _jsxs("div", { className: "mt-4", children: [_jsx("label", { className: "text-sm font-medium", children: l.email }), _jsx(Input, { type: "email", className: "mt-1", value: email, onChange: (e) => setEmail(e.target.value), required: true })] }), _jsxs("div", { className: "mt-4", children: [_jsx("label", { className: "text-sm font-medium", children: l.password }), _jsx(Input, { type: "password", className: "mt-1", value: password, onChange: (e) => setPassword(e.target.value), required: true })] }), _jsx(Button, { type: "submit", className: "w-full mt-4", disabled: loading, children: loading ? l.signingIn : l.register }), _jsx("div", { className: "mt-4", children: _jsx(Button, { variant: "ghost", type: "button", onClick: () => switchMode(AuthMode.SignIn), disabled: loading, children: l.alreadyHaveAccount }) }), renderGoogleDivider()] }));
    const renderForgotPassword = () => (_jsxs("form", { onSubmit: handleForgotPassword, children: [_jsx("h1", { className: "text-lg font-semibold mb-2", children: l.forgotPassword }), renderError(), resetEmailSent ? (_jsxs("div", { className: "text-center my-8", children: [_jsx(Alert, { className: "mb-2 border-green-200 text-green-800 bg-green-50", children: _jsx(AlertDescription, { children: l.resetEmailSent }) }), _jsx(Button, { type: "button", onClick: () => switchMode(AuthMode.SignIn), children: l.backToSignIn })] })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "mt-4", children: [_jsx("label", { className: "text-sm font-medium", children: l.email }), _jsx(Input, { type: "email", className: "mt-1", value: email, onChange: (e) => setEmail(e.target.value), required: true })] }), _jsx(Button, { type: "submit", className: "w-full mt-4", disabled: loading, children: loading ? l.signingIn : l.sendResetLink }), _jsx("div", { className: "mt-4", children: _jsx(Button, { variant: "ghost", type: "button", onClick: () => switchMode(AuthMode.SignIn), disabled: loading, children: l.backToSignIn }) })] }))] }));
    let content;
    switch (mode) {
        case AuthMode.SignIn:
            content = renderSignIn();
            break;
        case AuthMode.Register:
            content = renderRegister();
            break;
        case AuthMode.ForgotPassword:
            content = renderForgotPassword();
            break;
    }
    return (_jsx("div", { className: cn('flex flex-col gap-6', className), style: { minWidth: 300 }, ...props, children: content }));
}
//# sourceMappingURL=login-form.js.map